import React, { useMemo } from "react";
import Box from "@mui/material/Box";

const CROSSHAIR_COUNT = 4;
const TICK_COUNT = 8;
const SWEEP_MS = 14000;

/**
 * Radar-style backdrop for the letter wheel.
 * Concentric rings + rotating sweep beam (no helm handles).
 * Colors follow android `ShipHelm` / appearance radar* tokens when provided.
 */
export default function ShipHelm({ size, inset = 0, colors = null }) {
    const geometry = useMemo(() => {
        const c = size / 2;
        const outerR = Math.max(8, c - inset);
        const rings = [1, 0.78, 0.56, 0.34].map((t) => outerR * t);
        const hubOuter = outerR * 0.12;
        const hubInner = outerR * 0.055;

        const crosshairs = Array.from({ length: CROSSHAIR_COUNT }, (_, i) => {
            const angle = (i / CROSSHAIR_COUNT) * Math.PI * 2 - Math.PI / 2;
            const cos = Math.cos(angle);
            const sin = Math.sin(angle);
            return {
                key: `cross-${i}`,
                x1: c + hubOuter * cos,
                y1: c + hubOuter * sin,
                x2: c + outerR * cos,
                y2: c + outerR * sin,
            };
        });

        const ticks = Array.from({ length: TICK_COUNT }, (_, i) => {
            const angle = (i / TICK_COUNT) * Math.PI * 2 - Math.PI / 2 + Math.PI / TICK_COUNT;
            const cos = Math.cos(angle);
            const sin = Math.sin(angle);
            const inner = outerR * 0.9;
            return {
                key: `tick-${i}`,
                x1: c + inner * cos,
                y1: c + inner * sin,
                x2: c + outerR * cos,
                y2: c + outerR * sin,
            };
        });

        const sweepSpan = Math.PI * 0.62;
        const sweepStart = -Math.PI / 2 - sweepSpan;
        const sweepEnd = -Math.PI / 2;
        const sx = c + outerR * Math.cos(sweepStart);
        const sy = c + outerR * Math.sin(sweepStart);
        const ex = c + outerR * Math.cos(sweepEnd);
        const ey = c + outerR * Math.sin(sweepEnd);
        const sweepPath = `M ${c} ${c} L ${sx} ${sy} A ${outerR} ${outerR} 0 0 1 ${ex} ${ey} Z`;

        return {
            c,
            outerR,
            rings,
            hubOuter,
            hubInner,
            crosshairs,
            ticks,
            sweepPath,
            beamX: c,
            beamY: c - outerR,
        };
    }, [size, inset]);

    const {
        c,
        outerR,
        rings,
        hubOuter,
        hubInner,
        crosshairs,
        ticks,
        sweepPath,
        beamX,
        beamY,
    } = geometry;

    const stroke = colors?.radarStroke || "rgba(255, 255, 255, 0.88)";
    const strokeSoft = colors?.radarStrokeSoft || "rgba(236, 253, 245, 0.5)";
    const glassMid = colors?.radarGlassMid || "rgba(153,246,228,0.26)";
    const glassOuter = colors?.radarGlassOuter || "rgba(13,148,136,0.16)";
    const rim = colors?.radarRim || "rgba(204,251,241,0.7)";
    const hub = colors?.radarHub || "rgba(45, 212, 191, 0.55)";
    const sweepMid = colors?.radarSweepMid || "rgba(153,246,228,0.18)";
    const sweepLead = colors?.radarSweepLead || "rgba(255,255,255,0.55)";
    const strokeWidth = Math.max(1.8, size * 0.01);
    const uid = `radar-${Math.round(size)}-${Math.round(inset)}`;

    return (
        <Box
            aria-hidden
            sx={{
                position: "absolute",
                inset: 0,
                width: size,
                height: size,
                pointerEvents: "none",
                "@keyframes wwRadarSweep": {
                    from: { transform: "rotate(0deg)" },
                    to: { transform: "rotate(360deg)" },
                },
            }}
        >
            <svg width={size} height={size} style={{ position: "absolute", inset: 0 }}>
                <defs>
                    <radialGradient id={`${uid}-glass`} cx="42%" cy="38%" r="68%">
                        <stop offset="0%" stopColor="rgba(255,255,255,0.55)" />
                        <stop offset="40%" stopColor={glassMid} />
                        <stop offset="100%" stopColor={glassOuter} />
                    </radialGradient>
                </defs>

                <circle cx={c} cy={c} r={outerR} fill={`url(#${uid}-glass)`} />
            </svg>

            <Box
                sx={{
                    position: "absolute",
                    width: size,
                    height: size,
                    borderRadius: "50%",
                    overflow: "hidden",
                    animation: `wwRadarSweep ${SWEEP_MS}ms linear infinite`,
                }}
            >
                <svg width={size} height={size}>
                    <defs>
                        <radialGradient id={`${uid}-sweep`} cx="50%" cy="50%" r="50%">
                            <stop offset="0%" stopColor={sweepLead} />
                            <stop offset="45%" stopColor={sweepMid} />
                            <stop offset="100%" stopColor="rgba(148,163,184,0)" />
                        </radialGradient>
                    </defs>
                    <path d={sweepPath} fill={`url(#${uid}-sweep)`} opacity={0.95} />
                    <line
                        x1={c}
                        y1={c}
                        x2={beamX}
                        y2={beamY}
                        stroke="rgba(255,255,255,0.95)"
                        strokeWidth={Math.max(2.5, size * 0.014)}
                        strokeLinecap="round"
                        opacity={0.9}
                    />
                    <circle
                        cx={beamX}
                        cy={beamY}
                        r={Math.max(3, size * 0.014)}
                        fill="rgba(255,255,255,0.95)"
                    />
                </svg>
            </Box>

            <svg width={size} height={size} style={{ position: "absolute", inset: 0 }}>
                <defs>
                    <linearGradient id={`${uid}-rim`} x1="0%" y1="0%" x2="100%" y2="100%">
                        <stop offset="0%" stopColor="rgba(255,255,255,0.98)" />
                        <stop offset="100%" stopColor={rim} />
                    </linearGradient>
                </defs>
                {rings.map((r, i) => (
                    <circle
                        key={`ring-${i}`}
                        cx={c}
                        cy={c}
                        r={r}
                        fill="none"
                        stroke={i === 0 ? `url(#${uid}-rim)` : strokeSoft}
                        strokeWidth={i === 0 ? strokeWidth * 1.35 : strokeWidth * 0.85}
                    />
                ))}

                {crosshairs.map((s) => (
                    <line
                        key={s.key}
                        x1={s.x1}
                        y1={s.y1}
                        x2={s.x2}
                        y2={s.y2}
                        stroke={stroke}
                        strokeWidth={strokeWidth * 0.95}
                        strokeLinecap="round"
                        opacity={0.85}
                    />
                ))}

                {ticks.map((t) => (
                    <line
                        key={t.key}
                        x1={t.x1}
                        y1={t.y1}
                        x2={t.x2}
                        y2={t.y2}
                        stroke={strokeSoft}
                        strokeWidth={strokeWidth * 0.8}
                        strokeLinecap="round"
                    />
                ))}

                <circle
                    cx={c}
                    cy={c}
                    r={hubOuter}
                    fill="rgba(255,255,255,0.5)"
                    stroke={stroke}
                    strokeWidth={strokeWidth}
                />
                <circle
                    cx={c}
                    cy={c}
                    r={hubInner}
                    fill={hub}
                    stroke={stroke}
                    strokeWidth={strokeWidth * 0.7}
                />
            </svg>
        </Box>
    );
}
